<!-- Stacked Citations Panel - All citations visible at once -->
<template>
  <div class="citation-panel" v-if="citationHistory && citationHistory.length > 0">
    <div class="citation-summary" v-if="citationHistory.length > 1">
      <div class="citation-badge">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 9H4.5a2.5 2.5 0 0 0 0 5H6"/>
          <path d="M18 9h1.5a2.5 2.5 0 0 1 0 5H18"/>
          <path d="M8 9h8"/>
          <path d="M8 15h8"/>
        </svg>
        <span>{{ citationHistory.length }} Citations</span>
      </div>
    </div>

    <!-- Stacked Citations - Loop through all citations -->
    <div class="stacked-citations">
      <div
        v-for="(citation, index) in citationHistory"
        :key="index"
        class="citation-card"
        :class="{ 'expanded': expandedCitations.includes(index) }"
      >
        <!-- Citation Header -->
        <div class="citation-header">
          <div class="citation-badge">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 0 0 5H6"/>
              <path d="M18 9h1.5a2.5 2.5 0 0 1 0 5H18"/>
              <path d="M8 9h8"/>
              <path d="M8 15h8"/>
            </svg>
            <span>Citation {{ citation.id }}</span>
          </div>

          <div class="citation-url" v-if="citation.url">
            <a :href="citation.url" target="_blank" rel="noopener noreferrer">{{ formatUrl(citation.url) }}</a>
          </div>

          <!-- No expand toggle since all are expanded by default -->
        </div>

        <!-- Citation Content (always visible) -->
        <div class="citation-content">
          <!-- Citation thumbnail -->
          <div class="citation-thumbnail-container">
            <div class="citation-thumbnail" @click="openCitationUrl(citation)">
              <template v-if="getThumbnail(citation, index)">
                <img :src="getThumbnail(citation, index)" alt="Citation thumbnail" />
              </template>
              <template v-else>
                <!-- SVG Placeholder -->
                <div class="svg-placeholder" v-html="placeholderSvg"></div>
              </template>
            </div>
          </div>

          <!-- Citation text -->
          <div class="citation-text">
            <blockquote>{{ formatTextForCitation(citation.sourceText) }}</blockquote>
            <div class="citation-metadata" v-if="hasMetadata(citation)">
              <div class="metadata-item" v-if="citation.metadata && citation.metadata.title">
                <span class="metadata-label">Title:</span> {{ citation.metadata.title }}
              </div>
              <div class="metadata-item" v-if="citation.metadata && citation.metadata.author">
                <span class="metadata-label">Author:</span> {{ citation.metadata.author }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StackedCitationsPanel',
  props: {
    citationHistory: {
      type: Array,
      default: () => []
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    segmentCitationUrls: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      expandedCitations: [], // Will be populated with all indices on mount
      placeholderSvg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
        <!-- Background with gradient -->
        <defs>
          <linearGradient id="bg-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#3B82F6" stop-opacity="0.1"/>
            <stop offset="100%" stop-color="#3B82F6" stop-opacity="0.3"/>
          </linearGradient>

          <!-- Abstract pattern for background -->
          <pattern id="dots-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="1.5" fill="#3B82F6" fill-opacity="0.3"/>
            <circle cx="0" cy="0" r="1.5" fill="#3B82F6" fill-opacity="0.3"/>
            <circle cx="40" cy="0" r="1.5" fill="#3B82F6" fill-opacity="0.3"/>
            <circle cx="0" cy="40" r="1.5" fill="#3B82F6" fill-opacity="0.3"/>
            <circle cx="40" cy="40" r="1.5" fill="#3B82F6" fill-opacity="0.3"/>
          </pattern>
        </defs>

        <!-- Main background -->
        <rect width="800" height="400" fill="url(#bg-gradient)"/>

        <!-- Pattern overlay -->
        <rect width="800" height="400" fill="url(#dots-pattern)"/>

        <!-- Abstract decoration elements -->
        <path d="M0,250 Q200,150 400,250 T800,250" stroke="#3B82F6" stroke-width="3" fill="none" stroke-opacity="0.3"/>
        <path d="M0,270 Q200,170 400,270 T800,270" stroke="#3B82F6" stroke-width="2" fill="none" stroke-opacity="0.2"/>
        <path d="M0,230 Q200,130 400,230 T800,230" stroke="#3B82F6" stroke-width="2" fill="none" stroke-opacity="0.2"/>

        <!-- Link icon in the center -->
        <g transform="translate(400, 180)">
          <circle cx="0" cy="0" r="60" fill="white" fill-opacity="0.9"/>

          <!-- Link icon -->
          <g transform="translate(-25, -25) scale(1.2)">
            <path d="M10,40 L10,30 A20,20 0 0,1 30,10 L40,10" stroke="#3B82F6" stroke-width="5" fill="none" stroke-linecap="round"/>
            <path d="M40,10 L30,10 A20,20 0 0,0 10,30 L10,40" stroke="#3B82F6" stroke-width="5" fill="none" stroke-linecap="round"/>

            <path d="M40,10 L50,10 A20,20 0 0,1 70,30 L70,40" stroke="#3B82F6" stroke-width="5" fill="none" stroke-linecap="round"/>
            <path d="M70,40 L70,30 A20,20 0 0,0 50,10 L40,10" stroke="#3B82F6" stroke-width="5" fill="none" stroke-linecap="round"/>
          </g>
        </g>

        <!-- Text at the bottom -->
        <text x="400" y="300" font-family="Arial, sans-serif" font-size="20" fill="#D1D5DB" text-anchor="middle" font-weight="bold">Source Citation</text>
        <text x="400" y="330" font-family="Arial, sans-serif" font-size="16" fill="#D1D5DB" text-anchor="middle" opacity="0.7">No thumbnail available</text>
      </svg>`,
      // Cache for thumbnails to avoid recalculating
      thumbnailCache: {}
    };
  },
  methods: {
    toggleExpand(index) {
      const position = this.expandedCitations.indexOf(index);
      if (position !== -1) {
        // Only allow collapse if there's at least one citation still expanded
        if (this.expandedCitations.length > 1) {
          this.expandedCitations.splice(position, 1);
        }
      } else {
        this.expandedCitations.push(index);
      }
    },
    expandAll() {
      this.expandedCitations = this.citationHistory.map((_, index) => index);
    },
    collapseAll() {
      // Keep at least the first one expanded
      this.expandedCitations = [0];
    },
    formatTextForCitation(text) {
      const getDomain = (url) => {
        try {
          return new URL(url).hostname.replace('www.', '');
        } catch (e) {
          console.warn('Error parsing URL:', e);
          return url;
        }
      };

      // First check if this is an error message
      const errorPattern = /Failed to (?:extract text from|fetch) (.+?)(?:: .*)?$/;
      const errorMatch = text.match(errorPattern);

      if (errorMatch) {
        const domain = getDomain(errorMatch[1]);
        return `Failed to load content from ${domain}`;
      }

      // Extract formatted source URLs
      const urlMatches = [
        ...text.matchAll(/\[URL:\s*(https?:\/\/[^\]]+)\]/g),
        ...text.matchAll(/\[Source:\s*(https?:\/\/[^\]]+)\]/g)
      ];

      // Get unique domains from formatted sources
      const uniqueDomains = [...new Set(
        urlMatches.map(match => getDomain(match[1]))
      )];

      // Clean all URL patterns
      let cleanText = text
        .replace(/\[URL:\s*(https?:\/\/[^\]]+)\]/g, '')
        .replace(/\[Source:\s*(https?:\/\/[^\]]+)\]/g, '')
        .replace(/https?:\/\/[^\s)}\]]+/g, '')
        .trim();

      // Add unique sources at the end
      if (uniqueDomains.length > 0) {
        cleanText += ` Source: ${uniqueDomains.join(', ')}`;
      }

      return cleanText;
    },
    formatUrl(url) {
      if (!url) return '';

      try {
        const urlObj = new URL(url);
        // Return domain name without www. prefix
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        // If URL parsing fails, return the original truncated
        return url.length > 30 ? url.substring(0, 30) + '...' : url;
      }
    },
    openCitationUrl(citation) {
      if (citation && citation.url) {
        window.open(citation.url, '_blank', 'noopener,noreferrer');
      }
    },
    hasMetadata(citation) {
      return citation &&
        citation.metadata &&
        Object.keys(citation.metadata).length > 0;
    },
    getThumbnail(citation, index) {
      // Check cache first
      if (this.thumbnailCache[index] !== undefined) {
        return this.thumbnailCache[index];
      }

      // Helper function to get a random item from an array
      const getRandomItem = (array) => {
        if (!array || array.length === 0) return null;
        const randomIndex = Math.floor(Math.random() * array.length);
        return array[randomIndex];
      };

      let thumbnail = null;

      // Try specific matching strategies if we have a citation URL
      if (citation && citation.url && this.searchResults) {
        // Try to find the thumbnail in the search results
        for (const result of this.searchResults) {
          // Skip results without URLs
          if (!result.url) continue;

          // Check for exact match
          if (result.url === citation.url) {
            thumbnail = result.thumbnail || result.image;
            break;
          }

          // Check if one contains the other
          if (citation.url.includes(result.url) ||
              result.url.includes(citation.url)) {
            thumbnail = result.thumbnail || result.image;
            break;
          }

          // Check for domain match
          try {
            const citationDomain = new URL(citation.url).hostname;
            const resultDomain = new URL(result.url).hostname;

            if (citationDomain === resultDomain) {
              thumbnail = result.thumbnail || result.image;
              break;
            }
          } catch (error) {
            // Skip domain comparison if URL parsing fails
            continue;
          }
        }

        // If no match found in search results, check metadata
        if (!thumbnail && citation.metadata && citation.metadata.image) {
          thumbnail = citation.metadata.image;
        }
      }

      // Fallback: check for images in search results based on URL patterns
      if (!thumbnail) {
        const searchResultImages = this.searchResults?.filter(result => {
          if (!result.url) return false;
          if (result.isImage) return true;

          const url = result.url.toLowerCase();
          const imageExtensions = [
            '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
            '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
          ];

          return imageExtensions.some(ext => url.includes(ext)) ||
            [
              'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
              '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
              'media.licdn.com/dms/image', 'profile-images', 'user-images',
              'fbcdn.net', 'twimg.com', 'instagram.com/p/', 'unsplash.com',
              'pexels.com', 'imgur.com', 'pinimg.com', 'cloudfront.net',
              'cloudinary.com', 'googleusercontent.com', 'gravatar.com'
            ].some(pattern => url.includes(pattern));
        });

        if (searchResultImages && searchResultImages.length > 0) {
          const randomImage = getRandomItem(searchResultImages);
          thumbnail = randomImage.thumbnail || randomImage.image || randomImage.url;
        }
      }

      // Last fallback: any image from search results
      if (!thumbnail) {
        const resultsWithImages = this.searchResults?.filter(
          result => result.thumbnail || result.image
        );

        if (resultsWithImages && resultsWithImages.length > 0) {
          const randomResult = getRandomItem(resultsWithImages);
          thumbnail = randomResult.thumbnail || randomResult.image;
        }
      }

      // Cache the result
      this.thumbnailCache[index] = thumbnail;
      return thumbnail;
    }
  },
  watch: {
    // Update expanded state when citation history changes
    citationHistory: {
      handler(newVal) {
        if (newVal.length > 0) {
          // Ensure all citations are expanded by default
          this.expandedCitations = Array.from(
            { length: newVal.length },
            (_, index) => index
          );
        }

        // Reset thumbnail cache when citations change
        this.thumbnailCache = {};
      },
      deep: true
    }
  },
  mounted() {
    // Default expand all citations
    if (this.citationHistory.length > 0) {
      // Create array with indices for all citations [0,1,2,...]
      this.expandedCitations = Array.from(
        { length: this.citationHistory.length },
        (_, index) => index
      );
    }
  }
}
</script>

<style scoped>
.citation-panel {
  background-color: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
}

/* Background image styling */
.citation-panel::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('/static/Gemini_Generated_Image_mgaykmgaykmgaykm.jpeg');
  background-size: cover;
  background-position: center;
  opacity: 0.04;
  z-index: 0;
  border-radius: 8px;
  filter: blur(8px);
}

/* Gradient overlay */
.citation-panel::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%);
  z-index: 1;
  border-radius: 8px;
}

/* Make sure all content is above the background */
.citation-panel > * {
  position: relative;
  z-index: 2;
}

.citation-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(209, 213, 219, 0.5);
}

.citation-badge {
  display: flex;
  align-items: center;
  color: #3B82F6;
  font-weight: 500;
  font-size: 14px;
  gap: 6px;
}

.stacked-citations {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.citation-card {
  border: 1px solid rgba(209, 213, 219, 0.5);
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s ease;
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.citation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background-color: rgba(249, 250, 251, 0.7);
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
  transition: background-color 0.2s;
}

.citation-header:hover {
  background-color: rgba(243, 244, 246, 0.7);
}

.citation-url {
  font-size: 12px;
  color: #6B7280;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 200px;
  white-space: nowrap;
}

.citation-url a {
  color: #3B82F6;
  text-decoration: none;
  transition: color 0.2s;
}

.citation-url a:hover {
  color: #2563EB;
  text-decoration: underline;
}

.expand-toggle {
  color: #6B7280;
  transition: transform 0.3s;
}

.expanded .citation-header .expand-toggle {
  color: #3B82F6;
}

.citation-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.citation-thumbnail-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.citation-thumbnail {
  background-color: #F9FAFB;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  max-height: 400px; /* Limit height to avoid too large images */
}

.citation-thumbnail:hover {
  transform: scale(1.01);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.citation-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 200px;
}

.svg-placeholder {
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.citation-text {
  width: 100%;
}

blockquote {
  margin: 0;
  padding-left: 12px;
  font-style: italic;
  color: #4B5563;
  border-left: 2px solid #D1D5DB;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
}

.citation-metadata {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(209, 213, 219, 0.5);
  font-size: 12px;
}

.metadata-item {
  display: flex;
  gap: 4px;
  color: #6B7280;
}

.metadata-label {
  font-weight: 500;
  color: #4B5563;
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dark theme adjustments */
.theme--dark .citation-panel {
  background-color: #121212;
}

.theme--dark .citation-panel::after {
  background: #121212;
}

.theme--dark .citation-summary {
  border-bottom-color: rgba(75, 85, 99, 0.5);
}

.theme--dark .citation-card {
  background-color: #121212;
  border-color: rgba(75, 85, 99, 0.5);
}

.theme--dark .citation-header {
  background-color: rgba(30, 41, 59, 0.9);
  border-bottom-color: rgba(75, 85, 99, 0.5);
}

.theme--dark .citation-header:hover {
  background-color: rgba(51, 65, 85, 0.9);
}

.theme--dark .citation-badge {
  color: #60A5FA;
}

.theme--dark .citation-url {
  color: #9CA3AF;
}

.theme--dark .citation-url a {
  color: #60A5FA;
}

.theme--dark .citation-url a:hover {
  color: #93C5FD;
}

.theme--dark .citation-text blockquote {
  color: #D1D5DB;
  border-left-color: #4B5563;
}

.theme--dark .citation-thumbnail {
  background-color: #1F2937;
}

.theme--dark .citation-metadata {
  border-top-color: rgba(75, 85, 99, 0.5);
}

.theme--dark .metadata-item {
  color: #9CA3AF;
}

.theme--dark .metadata-label {
  color: #D1D5DB;
}

.theme--dark .expand-toggle {
  color: #9CA3AF;
}

.theme--dark .expanded .citation-header .expand-toggle {
  color: #60A5FA;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .citation-panel {
    padding: 12px;
  }

  .citation-card {
    margin-bottom: 8px;
  }

  .citation-header {
    padding: 10px 12px;
  }

  .citation-content {
    padding: 12px;
  }

  .citation-url {
    max-width: 140px;
  }
}
</style>
